import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';

interface PollQuestionResult {
  questionId: string;
  text: string;
  type: string;
  options?: string[];
  optionCounts?: number[];
  textAnswers?: string[];
  totalResponses: number;
}

interface PollResultsData {
  pollId: string;
  title: string;
  totalResponses: number;
  questions: PollQuestionResult[];
}

interface PollResultsViewProps {
  results: PollResultsData;
}

export function PollResultsView({ results }: PollResultsViewProps) {
  return (
    <div className="space-y-6">
      <div className="text-sm text-muted-foreground">
        {results.totalResponses} {results.totalResponses === 1 ? 'response' : 'responses'}
      </div>

      {results.questions.map((q) => (
        <Card key={q.questionId}>
          <CardHeader className="pb-2">
            <CardTitle className="text-sm font-medium">{q.text}</CardTitle>
            <p className="text-xs text-muted-foreground">
              {q.totalResponses} {q.totalResponses === 1 ? 'answer' : 'answers'}
            </p>
          </CardHeader>
          <CardContent>
            {q.type === 'free_text' ? (
              <TextResults answers={q.textAnswers || []} />
            ) : (
              <ChoiceResults
                options={q.options || []}
                counts={q.optionCounts || []}
                total={q.totalResponses}
              />
            )}
          </CardContent>
        </Card>
      ))}
    </div>
  );
}

function ChoiceResults({
  options,
  counts,
  total,
}: {
  options: string[];
  counts: number[];
  total: number;
}) {
  return (
    <div className="space-y-3">
      {options.map((opt, i) => {
        const count = counts[i] || 0;
        const pct = total > 0 ? Math.round((count / total) * 100) : 0;

        return (
          <div key={i} className="space-y-1">
            <div className="flex items-center justify-between text-sm">
              <span>{opt}</span>
              <span className="text-muted-foreground">
                {count} ({pct}%)
              </span>
            </div>
            <div className="h-2 bg-muted rounded-full overflow-hidden">
              <div
                className="h-full bg-primary rounded-full transition-all"
                style={{ width: `${pct}%` }}
              />
            </div>
          </div>
        );
      })}
    </div>
  );
}

function TextResults({ answers }: { answers: string[] }) {
  if (answers.length === 0) {
    return <p className="text-sm text-muted-foreground">No text responses yet.</p>;
  }

  return (
    <div className="space-y-2">
      {answers.map((text, i) => (
        <div key={i} className="p-3 bg-muted rounded-md text-sm">
          {text}
        </div>
      ))}
    </div>
  );
}
